import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CreditCard, 
  AlertTriangle, 
  Clock, 
  User,
  ExternalLink,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useSharedData } from '@/hooks/useSharedData';

interface CompensationNotification {
  _id: string;
  employee: {
    _id: string;
    profilePicture?: string;
    user: {
      firstName: string;
      lastName: string;
      employeeId: string;
    };
  };
  compensationInfo: {
    startDate: string;
    endDate: string;
    periodInMonths: string;
    notifyPeriodInDays: string;
    note: string;
  }[];
}

const CompensationNotifications = () => {
  const { hasPermission } = useAuthStore();
  const { employees, loading: sharedLoading } = useSharedData();
  const [notifications, setNotifications] = useState<CompensationNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // Show 6 notifications per page (3 columns × 2 rows)

  // Check if user has permission to view compensation notifications
  const canViewNotifications = hasPermission('EMPLOYEES_VIEW') || hasPermission('EMPLOYEES_EDIT');

  // Helper function to calculate days remaining until expiry
  const getDaysUntilExpiry = (endDate: string) => {
    if (!endDate) {
      return null;
    }
    
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };
  // Helper function to determine compensation status (same logic as agreements)
  const getCompensationStatus = (compensation: any) => {
    const daysUntilExpiry = getDaysUntilExpiry(compensation.endDate);
    const notifyDays = parseInt(compensation.notifyPeriodInDays || '0', 10);
    
    // Ensure daysUntilExpiry is not null before performing numerical comparisons
    if (daysUntilExpiry !== null) {
      if (daysUntilExpiry < 0) {
        return { status: 'expired', label: 'Expired', color: 'bg-red-50 text-red-600 border-red-200' };
      } else if (daysUntilExpiry <= Math.min(7, notifyDays * 0.3)) {
        return { status: 'urgent', label: 'Urgent', color: 'bg-orange-50 text-orange-600 border-orange-200' };
      } else if (daysUntilExpiry <= Math.min(15, notifyDays * 0.6)) {
      return { status: 'warning', label: 'Warning', color: 'bg-yellow-50 text-yellow-600 border-yellow-200' };
    } else if (daysUntilExpiry <= notifyDays) {
      return { status: 'notification', label: 'Needs Attention', color: 'bg-blue-50 text-blue-600 border-blue-200' };
    }
    }
    return { status: 'normal', label: 'Normal', color: 'bg-green-50 text-green-600 border-green-200' };
  };

  // Process compensation notifications
  const processCompensationNotifications = () => {
    if (!employees || employees.length === 0) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const compensationsNeedingAttention: CompensationNotification[] = [];

    employees.forEach(employee => {
      if (employee.compensationInfo && employee.compensationInfo.length > 0) {
        const sortedCompensations = employee.compensationInfo.sort((a: any, b: any) => {
          const dateA = new Date(a.startDate || 0);
          const dateB = new Date(b.startDate || 0);
          return dateB.getTime() - dateA.getTime(); // Newest first
        });

        const mostRecentCompensation = sortedCompensations[0];
        
        if (mostRecentCompensation && mostRecentCompensation.endDate) {
          const daysUntilExpiry = getDaysUntilExpiry(mostRecentCompensation.endDate);
          const notifyDays = parseInt(mostRecentCompensation.notifyPeriodInDays || '0', 10);
          
          // Use the same logic as agreements: show notification if within notify period
          if (daysUntilExpiry !== null && daysUntilExpiry <= notifyDays) {
            compensationsNeedingAttention.push({
              _id: employee._id,
              employee: {
                _id: employee._id,
                profilePicture: employee.profilePicture,
                user: employee.user
              },
              compensationInfo: [mostRecentCompensation] // Only show the most recent compensation
            });
          }
        }
      }
    });

    setNotifications(compensationsNeedingAttention);
    setLoading(false);
  };

  // Process notifications when employees data changes
  useEffect(() => {
    if (!sharedLoading && employees) {
      processCompensationNotifications();
    }
  }, [employees, sharedLoading]);

  const totalNotifications = notifications.length;
  const totalPages = Math.ceil(totalNotifications / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  const allCompensations = notifications.flatMap(notification => 
    notification.compensationInfo.map(compensation => ({
      ...compensation,
      employee: notification.employee,
      notificationId: notification._id
    }))
  );
  
  const paginatedCompensations = allCompensations.slice(startIndex, endIndex);

  // Don't render if user doesn't have permission
  if (!canViewNotifications) {
    return null;
  }

  // Show loading state
  if (loading || sharedLoading) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
              <CreditCard className="h-5 w-5" />
            </div>
            Compensation Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading compensation notifications...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show error state
  if (error) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg">
              <AlertTriangle className="h-5 w-5" />
            </div>
            Compensation Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (totalNotifications === 0) {
    return (
      <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-6">
          <CardTitle className="flex items-center gap-3 text-xl font-bold">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg">
              <CreditCard className="h-5 w-5" />
            </div>
            Compensation Notifications
            <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100 border-0">
              All Good
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Urgent Compensations</h3>
            <p className="text-gray-600">All employee compensations are up to date!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-white/80 backdrop-blur-sm">
      <CardHeader className="pb-6">
        <CardTitle className="flex items-center gap-3 text-xl font-bold">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-lg">
            <AlertTriangle className="h-5 w-5" />
          </div>
          Compensation Notifications
          <Badge variant="default" className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-0">
            {totalNotifications} Need Attention
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="space-y-0 max-h-96 overflow-y-auto">
          {paginatedCompensations.map((compensation, index) => {
            const statusInfo = getCompensationStatus(compensation);
            const daysUntilExpiry = getDaysUntilExpiry(compensation.endDate);
            
            return (
              <Link 
                key={`${compensation.notificationId}-${index}`}
                to={`/employee/${compensation.employee._id}`}
                className="block bg-white border-b border-gray-100 hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Profile Picture */}
                    <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:ring-2 group-hover:ring-blue-100 transition-all duration-200">
                      {compensation.employee.profilePicture ? (
                        <img
                          src={compensation.employee.profilePicture.startsWith('http') 
                            ? compensation.employee.profilePicture 
                            : `${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8000'}${compensation.employee.profilePicture}`}
                          alt={`${compensation.employee.user.firstName} ${compensation.employee.user.lastName}`}
                          className="h-12 w-12 rounded-full object-cover"
                          onError={(e) => { 
                            e.currentTarget.style.display = 'none';
                          }}
                        
                        />
                      ) : (
                        <User className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-base group-hover:text-blue-600 transition-colors duration-200">
                        {compensation.employee.user.firstName} {compensation.employee.user.lastName}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {compensation.employee.user.employeeId || 'Employee'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side content */}
                  <div className="flex items-center gap-4">
                    {/* Status Badge */}
                    <Badge
                      variant="outline"
                      className={`${(statusInfo as { color: string } | undefined)?.color ?? 'text-gray-600'} border-0 text-sm px-3 py-1.5 font-medium`}
                    >
                      {(statusInfo as { label: string } | undefined)?.label ?? 'Unknown Status'}
                    </Badge>
                    
                    {/* Time Info */}
                    <div className="text-sm text-gray-600 flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                      <Clock className="h-4 w-4" />
                      <span className="font-medium">
                        {daysUntilExpiry !== null
                          ? daysUntilExpiry < 0
                            ? `Expired ${Math.abs(daysUntilExpiry)}d ago`
                            : `${daysUntilExpiry}d left`
                          : 'N/A'
                        }
                      </span>
                    </div>
                    
                    {/* Arrow Icon */}
                    <div className="text-gray-400 group-hover:text-blue-500 transition-colors duration-200">
                      <ExternalLink className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="text-sm text-gray-500">
              {totalNotifications} compensation{totalNotifications !== 1 ? 's' : ''} need{totalNotifications !== 1 ? '' : 's'} attention
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CompensationNotifications;
